import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  NavigationExtras,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, fromEvent, merge, of } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  map,
  startWith,
} from 'rxjs/operators';

import * as fromStore from 'src/app/store';

/**
 * Type definition for redirect function that receives route params and returns a UrlTree or route path.
 */
export type MobileRedirectFn = (
  route: ActivatedRouteSnapshot,
) => UrlTree | string[] | { commands: string[]; extras?: NavigationExtras };

/**
 * Internal function to check if the current device is mobile.
 * Checks both the store value and window width directly for reliability.
 *
 * @returns An Observable that emits true if mobile (<= 766px), false otherwise
 */
const checkIsMobile = (): Observable<boolean> => {
  const store = inject<Store<fromStore.State>>(Store);

  // Check window width directly
  const checkWindowWidth = (): boolean => {
    if (typeof window !== 'undefined') {
      return window.innerWidth <= 766;
    }
    return false;
  };

  // Get browser size from store and convert to boolean (isMobile)
  const storeSize$ = store.select(fromStore.getBrowserSizeName).pipe(
    startWith(null),
    map((sizeName) => {
      if (sizeName === 'small') {
        return true; // Mobile
      }
      if (sizeName === null || sizeName === '') {
        // Store not initialized yet, check window width directly
        return checkWindowWidth();
      }
      return false; // Not mobile (medium or large)
    }),
  );

  // Listen to window resize events directly to catch size changes
  const resize$ =
    typeof window !== 'undefined'
      ? fromEvent(window, 'resize').pipe(
          debounceTime(100), // Debounce resize events
          map(() => checkWindowWidth()),
          startWith(checkWindowWidth()), // Check immediately
        )
      : of(checkWindowWidth());

  // Combine store size and resize events to continuously monitor
  // Merge both observables so we get updates from either source
  return merge(storeSize$, resize$).pipe(
    distinctUntilChanged(), // Only emit when the result actually changes
  );
};

/**
 * Type definition for the MobileOnly factory function.
 */
type IMobileOnly = () => CanActivateFn;

/**
 * Guard factory that restricts routes to mobile devices only.
 * Base implementation returns a boolean (true if mobile, false if not).
 *
 * @example
 * ```typescript
 * // Basic usage - returns boolean
 * const guard = MobileOnly();
 *
 * // In route configuration
 * {
 *   path: 'some-route',
 *   canActivate: [MobileOnly()],
 * }
 * ```
 *
 * @returns A CanActivateFn guard function that returns boolean
 */
export const MobileOnly: IMobileOnly & {
  /**
   * Creates a guard with automatic redirect when accessed from non-mobile devices.
   *
   * @example
   * ```typescript
   * // Custom redirect function
   * const guard = MobileOnly.onRejected(() => {
   *   return ['/tickets'];
   * });
   *
   * // Custom redirect with route params
   * const guard = MobileOnly.onRejected((route) => {
   *   const ticketId = route.params['ticketId'];
   *   return {
   *     commands: ['/tickets'],
   *     extras: { queryParams: { ticket: ticketId } },
   *   };
   * });
   * ```
   *
   * @param redirectFn - Function that receives the route and returns a UrlTree or route path
   * @returns A CanActivateFn guard function that redirects non-mobile users
   */
  onRejected: (redirectFn: MobileRedirectFn) => CanActivateFn;
} = () => {
  return (
    _route: ActivatedRouteSnapshot,
    _state: RouterStateSnapshot,
  ): Observable<boolean> => {
    return checkIsMobile();
  };
};

/**
 * Extension of MobileOnly that automatically redirects non-mobile users.
 *
 * When accessed from a non-mobile device, this guard will automatically navigate
 * the user to the specified redirect location.
 *
 * @param redirectFn - Function that receives the route and returns a UrlTree or route path
 * @returns A CanActivateFn guard function that redirects non-mobile users
 */
MobileOnly.onRejected = (redirectFn: MobileRedirectFn): CanActivateFn => {
  return (
    route: ActivatedRouteSnapshot,
    _state: RouterStateSnapshot,
  ): Observable<boolean | UrlTree> => {
    const router = inject(Router);

    return checkIsMobile().pipe(
      map((isMobile) => {
        if (isMobile) {
          return true;
        }

        // Non-mobile: redirect based on redirectFn
        const redirectTarget = redirectFn(route);
        if (redirectTarget instanceof UrlTree) {
          return redirectTarget;
        }
        if (Array.isArray(redirectTarget)) {
          return router.createUrlTree(redirectTarget);
        }
        // Handle object with commands and extras
        return router.createUrlTree(
          redirectTarget.commands,
          redirectTarget.extras,
        );
      }),
    );
  };
};
